import DefaultIyugaLogo from "@assets/images/iyuga.jpg";
import _ from "lodash";
import PropTypes from "prop-types";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import CreatePieChart from "../D3Wheels/createPie";
import { sampleData } from "./sampleData";

const WheelComponent = ({
  userData,
  chartData,
  onArcSelected,
  setChartData,
  maxSelectedArcs,
  showLabels,
  containerDivStyle,
  arcThickness,
  margin,
  height = "78vh",
  segmentHighlighting,
  width = "40vw",
  resize,
  onClickPortrait,
  config = {
    isNotDraggable: true,
    segmentHighlighting: segmentHighlighting,
    segmentLableOpacity: 1,
    segmentLableLimit: 25,
    inactiveOpacity: 0.2, // Default inactive opacity added to config
    showTooltips: true,
    centerGap: 0, // Controls the space between center image and segments
  },
}) => {
  const parentRef = useRef(null);
  const pieInstanceRef = useRef(null);
  const isViewMode = false;

  // Memoize config object to prevent unnecessary re-renders
  const memoizedConfig = useMemo(() => config, [config]);

  // Memoize userData to prevent unnecessary re-renders
  const memoizedUserData = useMemo(
    () => userData,
    [userData?.id, userData?.url]
  );

  // Memoize chartData for stability
  const memoizedChartData = useMemo(
    () => chartData,
    [
      // Only re-create if data structure changes
      JSON.stringify(chartData),
    ]
  );

  // Memoize container style
  const memoizedContainerStyle = useMemo(
    () => ({
      width,
      position: "relative",
      ...containerDivStyle,
      height: containerDivStyle?.height || height,
    }),
    [width, height, containerDivStyle]
  );

  // Memoize setChartData to prevent unnecessary re-renders
  const memoizedSetChartData = useCallback(
    (data) => {
      setChartData(data);
    },
    [setChartData]
  );

  // Memoize onArcSelected callback
  const memoizedOnArcSelected = useCallback(
    (data) => {
      if (onArcSelected) {
        onArcSelected(data);
      }
    },
    [onArcSelected]
  );

  // Resize handler (debounced)
  const updateSize = useCallback(() => {
    if (pieInstanceRef.current) {
      pieInstanceRef.current.setSvg();
      pieInstanceRef.current.updateArcs(memoizedChartData);
    }
  }, [memoizedChartData]);

  // Effect to handle resizing using ResizeObserver
  useEffect(() => {
    if (!resize || !parentRef.current) return;
    const observer = new ResizeObserver(updateSize);
    observer.observe(parentRef.current);

    return () => observer.disconnect();
  }, [resize, updateSize]);

  // Effect to initialize and update D3 instance
  useEffect(() => {
    if (!parentRef.current) return;

    if (!pieInstanceRef.current) {
      pieInstanceRef.current = new CreatePieChart(
        parentRef.current,
        memoizedChartData,
        memoizedUserData,
        memoizedSetChartData,
        isViewMode,
        memoizedOnArcSelected,
        maxSelectedArcs,
        () => {}, // Empty hover callback
        arcThickness,
        () => {},
        memoizedConfig,
        margin
      );
    } else {
      pieInstanceRef.current.setMode(isViewMode);
      pieInstanceRef.current.updateArcs(memoizedChartData);
      pieInstanceRef.current.manageLabels(showLabels);
      pieInstanceRef.current.setProfile(memoizedUserData);
    }
  }, [
    memoizedChartData,
    memoizedUserData,
    showLabels,
    memoizedConfig,
    maxSelectedArcs,
    arcThickness,
    margin,
    memoizedSetChartData,
    memoizedOnArcSelected,
  ]);

  // Memoize portrait click handler
  const memoizedPortraitClickHandler = useCallback(() => {
    if (onClickPortrait) {
      onClickPortrait();
    }
  }, [onClickPortrait]);

  // Memoize portrait style
  const portraitStyle = useMemo(
    () => ({
      width: `calc(${width} * 0.6)`,
      height: `calc(${width} * 0.6)`,
      top: `calc(${width} * 0.2)`,
      left: `calc(${width} * 0.2)`,
      backgroundColor: "transparent",
      position: "absolute",
      borderRadius: "50%",
      cursor: "pointer",
    }),
    [width]
  );

  return (
    <div ref={parentRef} style={memoizedContainerStyle}>
      {onClickPortrait && (
        <div onClick={memoizedPortraitClickHandler} style={portraitStyle} />
      )}
    </div>
  );
};

WheelComponent.propTypes = {
  userData: PropTypes.shape({
    id: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      selected: PropTypes.bool,
      pattern: PropTypes.string,
      title: PropTypes.string,
      icon: PropTypes.elementType,
    })
  ).isRequired,
  onArcSelected: PropTypes.func,
  setChartData: PropTypes.func.isRequired,
  maxSelectedArcs: PropTypes.number,
  showLabels: PropTypes.bool,
  containerDivStyle: PropTypes.object,
  arcThickness: PropTypes.number,
  margin: PropTypes.number,
  segmentHighlighting: PropTypes.bool,
  resize: PropTypes.bool,
  // config.centerGap: number (optional) - controls the space between center image and segments
};

WheelComponent.defaultProps = {
  chartData: sampleData,
  userData: {
    id: "1",
    url: DefaultIyugaLogo,
  },
  onArcSelected: () => {},
  maxSelectedArcs: 1,
  showLabels: false,
  containerDivStyle: {},
  arcThickness: -10,
  margin: 50,
  segmentHighlighting: false,
  resize: true,
  config: {
    isNotDraggable: true,
    segmentHighlighting: false,
    segmentLableOpacity: 1,
    segmentLableLimit: 25,
    inactiveOpacity: 0.2,
    showTooltips: true,
    centerGap: 0, // Default gap
  },
};

// Use a custom comparator for the memo to better control rerenders
export default React.memo(WheelComponent, (prevProps, nextProps) => {
  // Don't use JSON.stringify for performance reasons - it's slow for large objects
  // Instead, do targeted comparisons on critical properties

  // Compare userData
  if (
    prevProps.userData?.id !== nextProps.userData?.id ||
    prevProps.userData?.url !== nextProps.userData?.url
  ) {
    return false;
  }

  // Compare chart data length first (quick check)
  if (
    !prevProps.chartData ||
    !nextProps.chartData ||
    prevProps.chartData.length !== nextProps.chartData.length
  ) {
    return false;
  }

  // Then compare chart data items more efficiently
  for (let i = 0; i < prevProps.chartData.length; i++) {
    const prevItem = prevProps.chartData[i];
    const nextItem = nextProps.chartData[i];

    if (
      prevItem.id !== nextItem.id ||
      prevItem.value !== nextItem.value ||
      prevItem.color !== nextItem.color ||
      prevItem.selected !== nextItem.selected ||
      prevItem.position !== nextItem.position
    ) {
      return false;
    }
  }

  // Compare the remaining scalar props
  return (
    prevProps.maxSelectedArcs === nextProps.maxSelectedArcs &&
    prevProps.showLabels === nextProps.showLabels &&
    prevProps.arcThickness === nextProps.arcThickness &&
    prevProps.margin === nextProps.margin &&
    prevProps.segmentHighlighting === nextProps.segmentHighlighting
  );
});
